import { createRouter, createWebHistory, START_LOCATION } from 'vue-router'
import store from '@state/store'
import routes from './routes'
import scrollBehavior from './scroll-behavior'
import { hasAnyRole } from '@utils/roles'
import { mayOpenAdminArea } from '@utils/admin-area'
import { checkForUpdate } from '@utils/sw-update'

const router = createRouter({
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  history: createWebHistory(),
  routes,
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons, and land on a #section.
  scrollBehavior,
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // If this isn't an initial page load, start the route progress bar. Compared
  // against START_LOCATION rather than testing `routeFrom.name`: on the first
  // navigation that name is `undefined`, so the old `!== null` check was always
  // true and the bar started on the initial load too.
  if (routeFrom !== START_LOCATION) {
    store.dispatch('progress/startProgress')
  }
  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired)
  const adminRequired = routeTo.matched.some(
    (route) => route.meta.adminRequired
  )
  // Roles gate: any matched route may require one of a set of roles (by name),
  // e.g. meta.rolesRequired: ['DEVELOPER'].
  const rolesRequired = routeTo.matched.reduce(
    (acc, route) =>
      route.meta.rolesRequired ? acc.concat(route.meta.rolesRequired) : acc,
    []
  )
  // OR gate: access granted to an admin OR to anyone holding one of the named
  // roles (e.g. meta.rolesOrAdminRequired: ['DEVELOPER']). Unlike the AND
  // combination of adminRequired + rolesRequired, this passes on either.
  const rolesOrAdminRequired = routeTo.matched.reduce(
    (acc, route) =>
      route.meta.rolesOrAdminRequired
        ? acc.concat(route.meta.rolesOrAdminRequired)
        : acc,
    []
  )

  // Permission gate: every matched route may require module permissions, e.g.
  // meta.permissionRequired: 'loto.read' (or a list, all of them needed).
  // ADMIN and DEVELOPER hold every permission (src/utils/permissions.js).
  const permissionRequired = routeTo.matched.reduce(
    (acc, route) =>
      route.meta.permissionRequired
        ? acc.concat(route.meta.permissionRequired)
        : acc,
    []
  )

  // Any-of gate: each matched route's meta.anyPermissionRequired lists
  // permissions of which at least one is needed (on top of any above), e.g.
  // editing devices from either Arc Flash or Infrared.
  const anyPermissionRequired = routeTo.matched
    .map((route) => route.meta.anyPermissionRequired)
    .filter((list) => Array.isArray(list) && list.length)

  // The admin area: Admins' and Developers' only, like every page in it
  // (src/utils/admin-area.js; Angel, 2026-10-19).
  const adminAreaRequired = routeTo.matched.some(
    (route) => route.meta.adminAreaRequired
  )

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next()

  if (store.getters['auth/loggedIn']) {
    return store
      .dispatch('auth/validate')
      .then((validUser) => {
        if (!validUser) return redirectToLogin()
        if (authorized()) return next()
        denyUnauthorized()
      })
      .catch(() => {
        // validate() rejected. E.g. a token refresh that failed. Without a
        // catch here `next()` would never be called and the navigation would
        // hang silently. Degrade gracefully instead:
        //   - Offline: we can't re-authenticate anyway, so fall back to the
        //     cached identity (respecting adminRequired) rather than stranding
        //     the user. Client-side gating is UX, not the security boundary.
        //   - Online: treat it as a dead session and send them to login.
        const offline =
          typeof navigator !== 'undefined' && navigator.onLine === false
        if (offline && store.getters['auth/loggedIn'] && authorized()) {
          return next()
        }
        redirectToLogin()
      })
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  redirectToLogin()

  // Whether the current user satisfies the route's admin + role gates.
  // These gates are meta-driven (arbitrary role-name lists per route), so they
  // can't use the auth module's named predicates. But they share its matching
  // helper, so a plain-string role entry is understood here too.
  function authorized() {
    if (adminRequired && !store.getters['auth/isAdmin']) return false
    const roles = store.getters['auth/roles']
    if (rolesRequired.length && !hasAnyRole(roles, ...rolesRequired)) {
      return false
    }
    if (
      rolesOrAdminRequired.length &&
      !store.getters['auth/isAdmin'] &&
      !hasAnyRole(roles, ...rolesOrAdminRequired)
    ) {
      return false
    }
    const can = store.getters['auth/can']
    if (!permissionRequired.every((permission) => can(permission))) {
      return false
    }
    if (!anyPermissionRequired.every((list) => list.some((p) => can(p)))) {
      return false
    }
    if (
      adminAreaRequired &&
      !mayOpenAdminArea(can, store.getters['auth/isAdmin'])
    ) {
      return false
    }
    return true
  }

  function denyUnauthorized() {
    store.dispatch('messages/setNewMessage', {
      message: 'Unauthorized',
      type: 'error',
    })
    redirectToLogin()
  }

  function redirectToLogin() {
    // Pass the original route to the login component
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
  }
})

// After navigation is confirmed, but before resolving...
router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not.
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) {
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  store.dispatch('progress/endProgress')
  // Navigating is a free moment to ask whether a new build exists: the user is
  // between screens, so discovering one now means it can be applied while they
  // are still reading rather than minutes later. Throttled and a no-op without a
  // registered worker (dev, and any browser where registration failed), so this
  // costs nothing on the paths that cannot use it.
  checkForUpdate()
})

export default router
